'use client';

/**
 * GroupPresenceBar — "who is here / who is typing" strip for group chat
 * (#8198, realtime W1 identity layer).
 *
 * Renders a compact row of member avatars plus a live "… online" line and a
 * "… is typing…" line, resolving the raw `user_id`s from `useSessionRealtime`
 * to human names via `useSessionMembers`. Presentational only — it does not
 * open the socket or fetch presence itself; the integrator threads
 * `presentUserIds` / `typingUserIds` in as props and mounts it (only for group
 * sessions) into `ResearchSession`.
 *
 * a11y: avatars are decorative (`aria-hidden`) because the same names are
 * announced by the polite live-region text lines, so a screen reader hears each
 * name once, not twice.
 */

import { useTranslations } from 'next-intl';
import type { ReactElement } from 'react';

import { LexAvatar } from '@/components/chat/LexAvatar';
import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar';
import { useSessionMembers } from '@/hooks/useSessionMembers';

interface GroupPresenceBarProps {
  sessionId: string;
  /** user_ids currently connected (from useSessionRealtime; may include self). */
  presentUserIds: string[];
  /** user_ids currently typing (from useSessionRealtime; excludes self). */
  typingUserIds: string[];
  /**
   * The viewer's own user_id. Presence lists OTHER people who are here, so the
   * viewer is filtered out — you don't see yourself listed as "online". When
   * undefined, nobody is filtered (all present ids render).
   */
  selfUserId?: string | undefined;
  /**
   * True while @Lex is actively streaming a reply (#2607). Surfaces the Lexomat
   * avatar in the active bar so @Lex reads as a live participant, mirroring the
   * "online" treatment of human members.
   */
  aiActive?: boolean | undefined;
}

/** Max avatars rendered before collapsing the remainder into a "+N" chip. */
const MAX_AVATARS = 5;

/**
 * Two-letter initials for an avatar fallback. Single-word labels take their
 * first two chars; multi-word labels take first + last initials. Pure/testable.
 */
export function initialsFor(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return (first + last).toUpperCase();
}

export function GroupPresenceBar({
  sessionId,
  presentUserIds,
  selfUserId,
  aiActive,
}: GroupPresenceBarProps): ReactElement | null {
  const t = useTranslations('GroupPresenceBar');
  const { resolveName, membersById } = useSessionMembers(sessionId);

  // The stack is driven by the persisted ROSTER, not by live presence. Presence
  // is transient — it empties during every handshake and every reconnect — so
  // rendering from it made the group avatars blink out exactly when the channel
  // was least healthy. The roster is stable and cached, so the collaborators are
  // always on screen (the Miro treatment); liveness is expressed by the per-
  // avatar online badge instead of by the row's existence.
  const roster = Array.from(membersById.keys());
  // Presence can legitimately contain an id the cached roster hasn't caught up
  // with yet (a member added mid-session), so union the two rather than trusting
  // either alone. Self sorts first — you are the anchor of your own stack.
  const memberIds = Array.from(new Set([...roster, ...presentUserIds])).sort(
    (a, b) => Number(b === selfUserId) - Number(a === selfUserId)
  );

  const onlineIds = new Set(presentUserIds);

  if (memberIds.length === 0 && !aiActive) {
    return null;
  }

  const shownAvatars = memberIds.slice(0, MAX_AVATARS);
  const avatarOverflow = memberIds.length - shownAvatars.length;

  // Avatars only (#2607): the bar is a row of circular avatars, each carrying a
  // status badge to show it's online — no name/typing text. @Lex, when actively
  // streaming, joins the row (leftmost) with the same online treatment.
  return (
    <ul
      className='flex items-center -space-x-2'
      role='group'
      aria-label={t('regionLabel')}
    >
      {aiActive && (
        <li className='relative inline-block' title={t('aiResponding')}>
          <LexAvatar className='h-7 w-7 ring-2 ring-surface-base' />
          <AvatarBadge />
        </li>
      )}
      {shownAvatars.map((userId) => {
        const name = resolveName(userId);
        const avatarUrl = membersById.get(userId)?.avatarUrl;
        const isOnline = onlineIds.has(userId);
        const isSelf = userId === selfUserId;
        const label = isSelf ? t('you', { name }) : name;
        return (
          <li key={userId}>
            <Avatar
              title={isOnline ? t('online', { name: label }) : label}
              className='h-7 w-7 ring-2 ring-surface-base'
            >
              {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
              <AvatarFallback className='bg-surface-muted text-content-primary text-[0.625rem] font-semibold'>
                {initialsFor(name)}
              </AvatarFallback>
              {/* Badge marks LIVE presence only — an offline member still shows
                  their avatar, just without the dot. */}
              {isOnline && <AvatarBadge />}
            </Avatar>
          </li>
        );
      })}
      {avatarOverflow > 0 && (
        <li>
          <Avatar className='h-7 w-7 ring-2 ring-surface-base'>
            <AvatarFallback className='bg-surface-muted text-content-secondary text-[0.625rem] font-semibold'>
              {t('overflow', { count: avatarOverflow })}
            </AvatarFallback>
          </Avatar>
        </li>
      )}
    </ul>
  );
}
